import { Button, Card, Space, Typography } from 'antd';
import React from 'react';

import { HealthIndicator } from '@/components/common/HealthIndicator';
import { Pill } from '@/components/common/Pill';
import { ArrowDownIcon as ArrowDownOutlined } from '@/icons';
import { getStatusUpdateAuthorText } from '@/utils/dateFormatter';
import { normalizeHealthStatus } from '@/utils/statusConverters';
import { isWeekWithinEditWindow } from '@/utils/weeklyUpdateUtils';

import type { HealthStatusType } from './HealthStatusBadge';

const { Title, Text } = Typography;

export interface StatusEntry {
  status: HealthStatusType;
  label: string;
}

export interface StatusUpdate {
  weekOf: string;
  onSchedule?: { status: HealthStatusType; notes?: string };
  onBudget?: { status: HealthStatusType; notes?: string } | null;
  csat?: { status: HealthStatusType; notes?: string };
  teamHealth?: { status: HealthStatusType; notes?: string };
  createdBy: string;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  accomplishments?: string;
  isCurrentWeek?: boolean;
}

export interface WeeklyStatusUpdatesCardProps {
  updates: StatusUpdate[];
  currentUserEmail?: string;
  canViewWeeklyStatus?: boolean;
  canUpdateWeeklyStatus?: boolean;
  onViewUpdate?: (update: StatusUpdate) => void;
}

export function WeeklyStatusUpdatesCard({
  updates,
  currentUserEmail: _currentUserEmail,
  canViewWeeklyStatus = false,
  canUpdateWeeklyStatus = false,
  onViewUpdate,
}: Readonly<WeeklyStatusUpdatesCardProps>): React.ReactElement {
  const isCurrentWeek = (update: StatusUpdate) => {
    return update.isCurrentWeek ?? false;
  };

  const isEditableWeek = (update: StatusUpdate) =>
    isCurrentWeek(update) || isWeekWithinEditWindow(update.weekOf, false);

  if (!updates || updates.length === 0) {
    return (
      <Card className="weekly-status-card">
        <Text type="secondary">No weekly updates available</Text>
      </Card>
    );
  }

  return (
    <Card className="weekly-status-card">
      <div className="weekly-status-card__header">
        <Title level={5} className="weekly-status-card__title">
          Weekly Status Updates
        </Title>
        <Pill tone="neutral">Source: Manual</Pill>
      </div>

      <Space direction="vertical" size="middle" className="weekly-status-card__items">
        {updates.map((update, idx) => (
          <div
            key={update.weekOf ?? idx}
            className="weekly-status-card__item"
            style={{
              borderBottom: idx < updates.length - 1 ? '1px solid #f0f0f0' : 'none',
            }}
          >
            {/* Date */}
            <Text type="secondary" className="weekly-status-card__date">
              {update.weekOf}
            </Text>

            {/* Status badges */}
            <div className="weekly-status-card__statuses">
              {[
                { status: update.onSchedule?.status || 'pending', label: 'On Schedule' },
                { status: update.onBudget?.status || 'pending', label: 'On Budget' },
                { status: update.csat?.status || 'pending', label: 'CSAT' },
                {
                  status: update.teamHealth?.status || 'pending',
                  label: 'Team Health',
                },
              ].map((entry, statusIdx) => (
                <HealthIndicator
                  key={`${statusIdx}-${entry.status}`}
                  status={normalizeHealthStatus(entry.status)}
                  context={entry.label}
                />
              ))}
            </div>

            {/* Content */}
            <div className="weekly-status-card__content">
              <Text className="weekly-status-card__author">
                {getStatusUpdateAuthorText(
                  update.createdBy,
                  update.createdAt,
                  update.updatedBy,
                  update.updatedAt,
                )}
              </Text>
            </div>

            {/* Button logic — hidden entirely without weekly-status view access */}
            {isEditableWeek(update) && canUpdateWeeklyStatus ? (
              <Button
                type="default"
                size="small"
                iconPosition="end"
                icon={<ArrowDownOutlined />}
                onClick={() => onViewUpdate?.(update)}
              >
                Edit Weekly Update
              </Button>
            ) : (
              canViewWeeklyStatus && (
                <Button
                  type="default"
                  size="small"
                  iconPosition="end"
                  icon={<ArrowDownOutlined />}
                  onClick={() => onViewUpdate?.(update)}
                >
                  View Weekly Update
                </Button>
              )
            )}
          </div>
        ))}
      </Space>
    </Card>
  );
}
